import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import {
  X,
  Loader2,
  ExternalLink,
  CheckCircle,
  User,
  Calendar,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  useFormsAssignmentDetail,
  type FormsQuestion,
} from '@/hooks/useFormsAssignmentResponses';
import { formsResponseUrl } from '@/lib/formsUrls';
import { resolveAssignmentScores } from '@/lib/formsScoring';
import { FormsScoreDisplay } from './FormsScoreDisplay';

interface FormsResponseViewModalProps {
  clinicId: string;
  formsAssignmentId: string;
  templateName?: string;
  onClose: () => void;
}

function formatResponse(question: FormsQuestion, response: unknown): string {
  if (response === null || response === undefined || response === '') return '—';

  switch (question.type) {
    case 'multiple_choice': {
      const option = question.options?.find((o) => o.value === response);
      return option?.label ?? String(response);
    }
    case 'checkbox': {
      if (!Array.isArray(response)) return '—';
      return response
        .map((value) => {
          const opt = question.options?.find((o) => o.value === value);
          return opt?.label ?? String(value);
        })
        .join(', ');
    }
    case 'scale':
      return `${response} / ${question.scaleMax ?? 10}`;
    case 'date':
      try {
        return format(new Date(String(response)), 'dd. MMM yyyy', { locale: da });
      } catch {
        return String(response);
      }
    default:
      return String(response);
  }
}

export function FormsResponseViewModal({
  clinicId,
  formsAssignmentId,
  templateName,
  onClose,
}: FormsResponseViewModalProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useFormsAssignmentDetail(
    clinicId,
    formsAssignmentId,
  );

  const adminUrl = data?.admin_url ?? formsResponseUrl(formsAssignmentId);
  const title = data?.questionnaire?.name ?? templateName ?? t('forms.responses.title', 'Svar');

  const scoreResult = useMemo(() => {
    if (!data) return null;
    return resolveAssignmentScores(
      data.questionnaire?.questions ?? [],
      data.responses,
      data.questionnaire?.scoring_config,
      data.score_details,
    );
  }, [data]);

  const fmtDate = (s: string | null) =>
    s ? format(new Date(s), 'dd. MMM yyyy HH:mm', { locale: da }) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="min-w-0 pr-4">
            <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('forms.responses.subtitle', 'Patientens svar fra Clinio Forms')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={adminUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('forms.responses.open_in_forms', 'Åbn i Clinio Forms')}
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {t('forms.responses.load_error', 'Kunne ikke hente svar')}
                  </p>
                  <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                    {error?.message}
                  </p>
                  <a
                    href={adminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-red-800 underline dark:text-red-200"
                  >
                    <ExternalLink className="h-4 w-4" />
                    {t('forms.responses.open_in_forms_fallback', 'Åbn i Clinio Forms i stedet')}
                  </a>
                </div>
              </div>
            </div>
          )}

          {data && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800/50 sm:grid-cols-2">
                {data.respondent && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">{t('forms.responses.respondent', 'Respondent')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {data.respondent.first_name} {data.respondent.last_name}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">{t('forms.responses.completed', 'Udfyldt')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {fmtDate(data.completed_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    {t('forms.status.completed', 'Udfyldt')}
                  </span>
                </div>
              </div>

              {scoreResult && (
                <FormsScoreDisplay
                  domainScores={scoreResult.domainScores}
                  totalScore={scoreResult.totalScore}
                  title={title}
                />
              )}

              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('forms.responses.answers', 'Svar')}
                  </h4>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(data.questionnaire?.questions ?? []).map((question, index) => (
                    <div key={question.id} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {question.text}
                          </p>
                          <p className="mt-1.5 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                            {formatResponse(question, data.responses[question.id])}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(data.questionnaire?.questions ?? []).length === 0 && (
                    <p className="px-4 py-6 text-center text-sm text-gray-500">
                      {t('forms.responses.no_questions', 'Ingen spørgsmål fundet')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
            {t('common.close', 'Luk')}
          </Button>
        </div>
      </div>
    </div>
  );
}
